<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import PipelinesAPI from 'dashboard/api/masifi/pipelines';

// Motivos de pérdida del pipeline (admin). Select curado = data analizable
// ("¿por qué perdemos?" solo se responde con motivos consistentes). Renombrar
// CASCADEA al histórico de leads (el server actualiza las copias string).
const props = defineProps({
  pipeline: { type: Object, default: null },
});
const emit = defineEmits(['updated']);

const { t } = useI18n();

const MAX_REASONS = 50;
const MAX_LENGTH = 120;

const dialogRef = ref(null);
// rows: { original: string|null, current: string } — original null = nueva.
const rows = ref([]);
const newReason = ref('');
const isSaving = ref(false);

const open = () => {
  rows.value = (props.pipeline?.loss_reasons || []).map(reason => ({
    original: reason,
    current: reason,
  }));
  newReason.value = '';
  isSaving.value = false;
  dialogRef.value.open();
};
defineExpose({ open });

const addReason = () => {
  const reason = newReason.value.trim();
  if (!reason || rows.value.length >= MAX_REASONS) return;
  if (rows.value.some(row => row.current === reason)) return;
  rows.value.push({ original: null, current: reason });
  newReason.value = '';
};

const removeReason = index => {
  rows.value.splice(index, 1);
};

const renames = computed(() =>
  rows.value
    .filter(
      row =>
        row.original &&
        row.current.trim() &&
        row.current.trim() !== row.original
    )
    .map(row => ({ from: row.original, to: row.current.trim() }))
);

const save = async () => {
  if (isSaving.value) return;
  isSaving.value = true;
  try {
    const loss_reasons = rows.value
      .map(row => row.current.trim())
      .filter(Boolean);
    await PipelinesAPI.update(props.pipeline.id, {
      pipeline: { loss_reasons, loss_reason_renames: renames.value },
    });
    dialogRef.value.close();
    emit('updated', loss_reasons);
    useAlert(t('LEADS.LOSS_REASONS.SAVED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        t('LEADS.PIPELINE.ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('LEADS.LOSS_REASONS.TITLE')"
    :description="t('LEADS.LOSS_REASONS.DESCRIPTION')"
    :confirm-button-label="t('LEADS.LOSS_REASONS.SAVE')"
    :is-loading="isSaving"
    @confirm="save"
  >
    <div class="flex flex-col gap-2">
      <div
        v-for="(row, index) in rows"
        :key="index"
        class="flex items-center gap-2"
      >
        <Input v-model="row.current" class="flex-1" :maxlength="MAX_LENGTH" />
        <Button
          ghost
          slate
          icon="i-lucide-trash-2"
          size="sm"
          :title="t('LEADS.LOSS_REASONS.REMOVE')"
          @click="removeReason(index)"
        />
      </div>
      <p v-if="!rows.length" class="py-2 text-xs text-center text-n-slate-10">
        {{ t('LEADS.LOSS_REASONS.EMPTY') }}
      </p>
      <div class="flex items-center gap-2">
        <Input
          v-model="newReason"
          class="flex-1"
          :maxlength="MAX_LENGTH"
          :placeholder="t('LEADS.LOSS_REASONS.ADD_PLACEHOLDER')"
          @keyup.enter="addReason"
        />
        <Button
          faded
          slate
          icon="i-lucide-plus"
          size="sm"
          :disabled="!newReason.trim() || rows.length >= MAX_REASONS"
          @click="addReason"
        />
      </div>
      <p v-if="renames.length" class="text-xs text-n-slate-10">
        {{ t('LEADS.LOSS_REASONS.RENAME_HINT', { count: renames.length }) }}
      </p>
    </div>
  </Dialog>
</template>
